import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { CopyToClipboard } from "@/lib/Components/Common/CopyToClipboard";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import {
  createRefundPaymentMutation,
  refundsQuery,
} from "@/app/Refunds/GraphQL/refundMutations";
import { useBooking } from "@/app/Bookings/Hooks/useBooking";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { DateInput } from "@/lib/Components/Form/Inputs/DateInput";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import {
  ModelType,
  RefundPaymentMethod,
  RefundType,
  CreateRefundPaymentInput,
} from "@/gql/graphql";
import dayjs from "dayjs";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { EmptyState } from "@/lib/Components/EmptyState/EmptyState";
import {
  DownloadIcon,
  LandmarkIcon,
  PencilIcon,
  ReceiptIcon,
} from "lucide-react";
import { Suspense } from "react";
import { ReceiptCard } from "@/app/Refunds/Components/ReceiptCard";
import { EditReceiptDialog } from "@/app/Refunds/Components/EditReceiptDialog";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { Button } from "@/components/catalyst/button";
import { Badge } from "@/components/catalyst/badge";
import { bookingStatusColorMap } from "@/app/Bookings/Utils/bookingStatusColorMap";
import { toFormikValidate } from "@/lib/Zod/zod-formik-adapter";
import { z } from "zod";
import { useTranslations } from "use-intl";

const paymentValidation = z.object({
  amount: z.number().min(1, "Amount is required"),
  transaction_number: z
    .string()
    .nullish()
    .refine((val) => val && val.length > 0, "Transaction number is required"),
});

export function ProcessRefundDialog({
  bookingId,
  refundType,
}: {
  bookingId: string;
  refundType: RefundType;
}) {
  const title =
    refundType === RefundType.Fuel
      ? "Process Fuel Refund"
      : "Process Ferry Refund";
  return (
    <BaseDialog title={title} size="2xl">
      <Suspense fallback={<div className="p-4 text-center">Loading...</div>}>
        <ProcessRefundContent bookingId={bookingId} refundType={refundType} />
      </Suspense>
    </BaseDialog>
  );
}

function ProcessRefundContent({
  bookingId,
  refundType,
}: {
  bookingId: string;
  refundType: RefundType;
}) {
  const { mutateAsync: createPayment } = useGqlMutation(
    createRefundPaymentMutation,
  );
  const { close } = useDialogState();
  const { open: openEditReceiptDialog } = useDialog(EditReceiptDialog);
  const t = useTranslations("booking");

  const { data: refundData } = useSuspenseGqlQuery(refundsQuery, {
    object_type: ModelType.Booking,
    object_id: bookingId,
  });
  const booking = useBooking(bookingId);

  const refund = refundData?.refunds.find((r) => r.type === refundType);

  if (!refund) {
    return (
      <EmptyState
        Icon={ReceiptIcon}
        header="No refund found"
        subheader="This booking does not have a refund request"
      />
    );
  }

  const receipts = refund.receipts ?? [];
  const sortedReceipts = [...receipts].sort((a, b) => {
    if (!a.date) return 1;
    if (!b.date) return -1;
    return dayjs(a.date).valueOf() - dayjs(b.date).valueOf();
  });
  const receiptDateCounts = receipts.reduce<Record<string, number>>(
    (acc, r) => {
      if (r.date) {
        acc[r.date] = (acc[r.date] ?? 0) + 1;
      }
      return acc;
    },
    {},
  );
  const currency = refund.currency;
  const payments = refund.payments ?? [];
  const totalPayments = payments.reduce((sum, p) => sum + p.amount, 0);
  const remainingAmount = refund.refund_amount - totalPayments;

  const bookingDates =
    booking?.depart_at && booking?.deliver_at
      ? `${dayjs(booking.depart_at).format("DD MMM YYYY")} - ${dayjs(
          booking.deliver_at,
        ).format("DD MMM YYYY")}`
      : null;

  return (
    <div className="space-y-6">
      {/* Receipts Section */}
      <div>
        <h3 className="mb-3 text-sm font-medium text-zinc-700 dark:text-zinc-300">
          Receipts
        </h3>
        {receipts.length === 0 ? (
          <EmptyState
            Icon={ReceiptIcon}
            header="No receipts"
            subheader="No receipts have been uploaded"
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {sortedReceipts.map((receipt) => {
              const hasDateWarning =
                receipt.date && booking?.depart_at && booking?.deliver_at
                  ? dayjs(receipt.date).isBefore(
                      dayjs(booking.depart_at),
                      "day",
                    ) ||
                    dayjs(receipt.date).isAfter(
                      dayjs(booking.deliver_at),
                      "day",
                    )
                  : false;

              const hasCurrencyMismatch =
                receipt.currency && receipt.currency !== currency;

              const hasDuplicateDate = receipt.date
                ? (receiptDateCounts[receipt.date] ?? 0) > 1
                : false;

              return (
                <ReceiptCard
                  key={receipt.id}
                  receipt={receipt}
                  warnings={{
                    hasDateWarning,
                    hasCurrencyMismatch,
                    hasDuplicateDate,
                    bookingCurrency: currency,
                  }}
                  actions={
                    <>
                      <Button
                        plain
                        onClick={() =>
                          openEditReceiptDialog({
                            receiptId: receipt.id,
                          })
                        }
                      >
                        <PencilIcon data-slot="icon" />
                      </Button>

                      {receipt.image ? (
                        <Button
                          plain
                          onClick={async () => {
                            const response = await fetch(receipt.image!.url);
                            const blob = await response.blob();
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `receipt-${receipt.id}.${blob.type.split("/")[1] || "jpg"}`;
                            a.click();
                            URL.revokeObjectURL(url);
                          }}
                        >
                          <DownloadIcon data-slot="icon" />
                        </Button>
                      ) : null}
                    </>
                  }
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Refund Summary */}
      <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-700 dark:bg-zinc-800/50">
        <div className="flex items-center justify-between">
          <span className="text-sm text-zinc-600 dark:text-zinc-400">
            Maximum Refund
          </span>
          <span className="font-medium">
            {formatCurrency(refund.maximum_refund_amount, currency)}
          </span>
        </div>
        <div className="mt-2 flex items-center justify-between">
          <span className="text-sm text-zinc-600 dark:text-zinc-400">
            Refund Amount
          </span>
          <span className="font-semibold text-green-600">
            {formatCurrency(refund.refund_amount, currency)}
          </span>
        </div>
        {totalPayments > 0 ? (
          <>
            <div className="mt-2 flex items-center justify-between">
              <span className="text-sm text-zinc-600 dark:text-zinc-400">
                Already Paid
              </span>
              <span className="font-medium">
                {formatCurrency(totalPayments, currency)}
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between border-t border-zinc-200 pt-2 dark:border-zinc-700">
              <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                Remaining
              </span>
              <span
                className={`font-semibold ${
                  remainingAmount > 0
                    ? "text-amber-600"
                    : remainingAmount < 0
                      ? "text-red-600"
                      : "text-green-600"
                }`}
              >
                {formatCurrency(remainingAmount, currency, { showZero: true })}
              </span>
            </div>
          </>
        ) : null}
      </div>

      {/* Booking & Customer Details */}
      <div>
        <h3 className="mb-3 text-sm font-medium text-zinc-700 dark:text-zinc-300">
          Booking & Customer
        </h3>
        <div className="rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-zinc-500">Booking Number</span>
              <CopyToClipboard text={booking?.reference ?? null}>
                <div className="font-medium">{booking?.reference || "--"}</div>
              </CopyToClipboard>
            </div>
            <div>
              <span className="text-zinc-500">Booking Dates</span>
              <CopyToClipboard text={bookingDates}>
                <div className="font-medium">{bookingDates || "--"}</div>
              </CopyToClipboard>
            </div>
            <div>
              <span className="text-zinc-500">Status</span>
              <div className="mt-0.5">
                {booking?.status ? (
                  <Badge color={bookingStatusColorMap[booking.status]}>
                    {t(`status.${booking.status}`)}
                  </Badge>
                ) : (
                  <div className="font-medium">--</div>
                )}
              </div>
            </div>
            <div>
              <span className="text-zinc-500">Customer Name</span>
              <CopyToClipboard text={booking?.name ?? null}>
                <div className="font-medium">{booking?.name || "--"}</div>
              </CopyToClipboard>
            </div>
            <div>
              <span className="text-zinc-500">Email</span>
              <CopyToClipboard text={booking?.email ?? null}>
                <div className="font-medium">{booking?.email || "--"}</div>
              </CopyToClipboard>
            </div>
            <div className="col-span-2">
              <span className="text-zinc-500">Address</span>
              <CopyToClipboard text={booking?.address ?? null}>
                <div className="font-medium">{booking?.address || "--"}</div>
              </CopyToClipboard>
            </div>
          </div>
        </div>
      </div>

      {/* Bank Details */}
      <div>
        <h3 className="mb-3 text-sm font-medium text-zinc-700 dark:text-zinc-300">
          Bank Details
        </h3>
        <div className="rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
          <div className="flex items-start gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/20">
              <LandmarkIcon className="size-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="grid flex-1 grid-cols-2 gap-3 text-sm">
              <div className="col-span-2">
                <span className="text-zinc-500">Country</span>
                <CopyToClipboard text={refund.bank_country ?? null}>
                  <div className="font-medium">
                    {refund.bank_country || "--"}
                  </div>
                </CopyToClipboard>
              </div>
              <div>
                <span className="text-zinc-500">Bank Name</span>
                <CopyToClipboard text={refund.bank_name ?? null}>
                  <div className="font-medium">{refund.bank_name || "--"}</div>
                </CopyToClipboard>
              </div>
              <div>
                <span className="text-zinc-500">Account Name</span>
                <CopyToClipboard text={refund.bank_account_name ?? null}>
                  <div className="font-medium">
                    {refund.bank_account_name || "--"}
                  </div>
                </CopyToClipboard>
              </div>
              <div>
                <span className="text-zinc-500">Account Number</span>
                <CopyToClipboard text={refund.bank_account_number ?? null}>
                  <div className="font-medium">
                    {refund.bank_account_number || "--"}
                  </div>
                </CopyToClipboard>
              </div>
              <div>
                <span className="text-zinc-500">BSB / Sort Code</span>
                <CopyToClipboard text={refund.bank_identifier_code ?? null}>
                  <div className="font-medium">
                    {refund.bank_identifier_code || "--"}
                  </div>
                </CopyToClipboard>
              </div>
              <div className="col-span-2">
                <span className="text-zinc-500">Recipient Address</span>
                <CopyToClipboard text={refund.recipient_address ?? null}>
                  <div className="whitespace-pre-line font-medium">
                    {refund.recipient_address || "--"}
                  </div>
                </CopyToClipboard>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Form */}
      <div>
        <h3 className="mb-3 text-sm font-medium text-zinc-700 dark:text-zinc-300">
          Record Payment
        </h3>
        <GenericForm<CreateRefundPaymentInput>
          validate={toFormikValidate(paymentValidation)}
          initialValues={{
            refund_form_id: refund.id,
            method: RefundPaymentMethod.DirectDeposit,
            amount:
              remainingAmount > 0 ? remainingAmount : refund.refund_amount,
            currency: currency,
            refunded_at: dayjs().format("YYYY-MM-DD"),
            transaction_number: null,
          }}
          onSubmit={async (values) => {
            await createPayment({
              input: values,
            });

            close();
          }}
        >
          <CurrencyInput name="amount" label="Amount" currency={currency} />
          <DateInput
            name="refunded_at"
            label="Refunded At"
            max={dayjs().format("YYYY-MM-DD")}
          />
          <TextInput
            name="transaction_number"
            label="Transaction Number"
            className="col-span-full"
          />
          <DialogSubmitButton text="Record Payment & Complete Task" />
        </GenericForm>
      </div>
    </div>
  );
}
